/**
 * Enhanced Public Survey Page - Token-based public form for suppliers
 * 
 * Features:
 * - Welcome message section
 * - Progress indicator with percentage
 * - Multiple question types (numeric, text, select, multi_select, boolean, date, file_upload)
 * - Save draft functionality
 * - Question navigation dots
 * - Mobile-responsive design
 * 
 * Connected to backend APIs:
 * - GET /api/v1/supplier-portal/public/survey/:token
 * - POST /api/v1/supplier-portal/public/survey/:token/submit
 * - POST /api/v1/supplier-portal/public/survey/:token/save-draft
 */
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 

  Globe,
  Building2,
  CheckCircle,
  AlertCircle,
  RefreshCw,
  Upload,
  ChevronRight,
  ChevronLeft,
  Send,
  Shield,
  Clock,
  FileText,
  HelpCircle,
  Calendar,
  X,
  Info,
  Save,
  Check,
  Mail,
  Trash2
} from 'lucide-react';
import { apiFetch } from '../../../services/api_service';

const API = process.env.REACT_APP_BACKEND_URL;

export default function PublicSurveyPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  
  const [surveyData, setSurveyData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [responses, setResponses] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState({});
  const [startTime] = useState(new Date());
  const [showWelcome, setShowWelcome] = useState(true);

  useEffect(() => {
    if (token) {
      fetchSurvey();
    }
  }, [token]);

  const fetchSurvey = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API}/api/v1/supplier-portal/public/survey/${token}`);
      
      if (!response.ok) {
        // Map status codes to user-friendly messages
        let errorMessage = 'Failed to load survey';
        
        if (response.status === 401) {
          errorMessage = 'Invalid or expired survey link';
        } else if (response.status === 404) {
          errorMessage = 'Survey not found or link has expired';
        } else if (response.status === 410) {
          errorMessage = 'This survey has been closed';
        } else if (response.status === 403) {
          errorMessage = 'Access denied. Your survey link may be invalid.';
        }
        
        throw new Error(errorMessage);
      }

      const data = await response.json();
      setSurveyData(data);
      
      // Initialize responses from existing data or empty
      const initialResponses = {};
      (data.survey?.configuration?.questions || []).forEach(q => {
        if (data.existingResponse?.responses) {
          const existing = data.existingResponse.responses.find(r => r.questionId === q.questionId);
          initialResponses[q.questionId] = existing?.value || '';
        } else {
          initialResponses[q.questionId] = '';
        }
      });
      setResponses(initialResponses);
      
      // Skip welcome if resuming
      if (data.existingResponse) {
        setShowWelcome(false);
      }
    } catch (err) {
      console.error('Error loading survey:', err);
      // Ensure user-friendly error message is shown
      let errorMessage = err.message;
      if (errorMessage.includes('clone') || errorMessage.includes('Response body') || errorMessage.includes('TypeError')) {
        errorMessage = 'Invalid or expired survey link';
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleResponseChange = (questionId, value) => {
    setResponses(prev => ({
      ...prev,
      [questionId]: value
    }));
  };

  const handleFileUpload = async (questionId, files) => {
    // Store file references locally
    setUploadedFiles(prev => ({
      ...prev,
      [questionId]: Array.from(files)
    }));
    handleResponseChange(questionId, Array.from(files).map(f => ({ 
      fileName: f.name, 
      fileSize: f.size,
      fileType: f.type 
    })));
  };

  const removeFile = (questionId, fileIndex) => {
    setUploadedFiles(prev => {
      const updated = { ...prev };
      if (updated[questionId]) {
        updated[questionId] = updated[questionId].filter((_, i) => i !== fileIndex);
      }
      return updated;
    });
    
    const currentFiles = responses[questionId] || [];
    handleResponseChange(questionId, currentFiles.filter((_, i) => i !== fileIndex));
  };

  const handleSaveDraft = async () => {
    try {
      setSavingDraft(true);
      
      const formattedResponses = Object.entries(responses).map(([questionId, value]) => ({
        questionId,
        value
      }));

      const response = await fetch(`${API}/api/v1/supplier-portal/public/survey/${token}/save-draft`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          responses: formattedResponses,
          startedAt: startTime.toISOString()
        })
      });

      if (response.ok) {
        alert('✅ Draft saved! You can return later to complete the survey.');
      } else {
        throw new Error('Failed to save draft');
      }
    } catch (err) {
      console.error('Error saving draft:', err);
      alert('❌ Failed to save draft. Please try again.');
    } finally {
      setSavingDraft(false);
    }
  };

  const handleSubmit = async () => {
    const survey = surveyData?.survey;
    const questions = survey?.configuration?.questions || [];
    
    // Validate required fields
    const requiredQuestions = questions.filter(q => q.required);
    const missingRequired = requiredQuestions.filter(q => {
      const value = responses[q.questionId];
      return value === '' || value === null || value === undefined || 
        (Array.isArray(value) && value.length === 0);
    });

    if (missingRequired.length > 0) {
      alert(`Please complete the following required fields:\n${missingRequired.map(q => `• ${q.questionText}`).join('\n')}`);
      // Navigate to first missing question
      const firstMissingIndex = questions.findIndex(q => q.questionId === missingRequired[0].questionId);
      if (firstMissingIndex >= 0) {
        setCurrentQuestionIndex(firstMissingIndex);
      }
      return;
    }

    if (!window.confirm('Submit your responses? This action cannot be undone.')) {
      return;
    }

    try {
      setSubmitting(true);
      
      const formattedResponses = Object.entries(responses).map(([questionId, value]) => ({
        questionId,
        value,
        ...(uploadedFiles[questionId] ? { 
          evidenceAttached: uploadedFiles[questionId].map(f => ({ 
            filename: f.name,
            fileSize: f.size 
          })) 
        } : {})
      }));

      const response = await fetch(`${API}/api/v1/supplier-portal/public/survey/${token}/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          responses: formattedResponses,
          submittedBy: {
            contactName: surveyData.supplier?.primaryContact?.name || 'Unknown',
            contactEmail: surveyData.supplier?.primaryContact?.email || 'unknown@example.com',
            contactRole: surveyData.supplier?.primaryContact?.title || ''
          },
          startedAt: startTime.toISOString()
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to submit survey');
      }

      // Navigate to thank you page
      navigate(`/supplier-survey/${token}/thank-you`);
    } catch (err) {
      console.error('Error submitting survey:', err);
      alert('❌ Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="h-12 w-12 border-4 border-gray-300 border-t-purple-600 rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading survey...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8 text-center">
          <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="text-red-500" size={32} />
          </div>
          <h1 className="text-xl font-bold text-gray-900 mb-2">Survey Not Available</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <p className="text-sm text-gray-500">
            If you believe this is an error, please contact the survey administrator.
          </p>
        </div>
      </div>
    );
  }

  const survey = surveyData?.survey;
  const supplier = surveyData?.supplier;
  const questions = survey?.configuration?.questions || [];
  const currentQuestion = questions[currentQuestionIndex];
  
  // Calculate progress
  const answeredQuestions = Object.keys(responses).filter(qId => {
    const value = responses[qId];
    return value !== '' && value !== null && value !== undefined && 
      !(Array.isArray(value) && value.length === 0);
  }).length;
  const progressPercent = questions.length > 0 ? Math.round((answeredQuestions / questions.length) * 100) : 0;
  
  const isLastQuestion = currentQuestionIndex === questions.length - 1;

  // Check if all required questions are answered
  const allRequiredAnswered = questions
    .filter(q => q.required)
    .every(q => {
      const value = responses[q.questionId];
      return value !== '' && value !== null && value !== undefined &&
        !(Array.isArray(value) && value.length === 0);
    });

  return (
    <div className="min-h-screen bg-gray-50" data-testid="public-survey-page">
      {/* Header */}
      <header className="bg-gradient-to-br from-purple-700 to-purple-600 text-white p-6">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-2xl font-bold mb-1">{survey?.surveyName}</h1>
              <p className="text-white/90 text-sm flex items-center gap-2">
                <Building2 size={14} />
                For: {supplier?.supplierName || 'Unknown Supplier'}
              </p>
            </div>
            <div className="text-right">
              <div className="text-4xl font-bold">{progressPercent}%</div>
              <p className="text-xs text-white/80">Complete</p>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="h-2 bg-white/20 rounded-full overflow-hidden">
            <div
              className="h-full bg-white transition-all duration-300 rounded-full"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        </div>
      </header>

      {/* Welcome Message */}
      {showWelcome && currentQuestionIndex === 0 && (
        <div className="bg-white border-b p-6">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-start gap-4">
              <span className="text-4xl">👋</span>
              <div>
                <h2 className="text-xl font-semibold text-gray-900 mb-2">Welcome!</h2>
                <p className="text-gray-700 mb-3">
                  {survey?.purpose || 'Thank you for participating in our sustainability data collection survey. Your responses help us understand and improve our environmental impact across the value chain.'}
                </p>
                <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600">
                  <span className="flex items-center gap-1">
                    <FileText size={14} />
                    {questions.length} questions
                  </span>
                  <span className="flex items-center gap-1">
                    <Clock size={14} />
                    Est. time: 15-20 minutes
                  </span>
                  {survey?.timeline?.dueDate && (
                    <span className="flex items-center gap-1 text-amber-600">
                      <Calendar size={14} />
                      Due: {new Date(survey.timeline.dueDate).toLocaleDateString()}
                    </span>
                  )}
                </div>
                <button
                  onClick={() => setShowWelcome(false)}
                  className="mt-4 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                >
                  Get Started →
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Question Content */}
      {(!showWelcome || currentQuestionIndex > 0) && (
        <main className="py-8">
          <div className="max-w-4xl mx-auto px-4">
            <div className="bg-white rounded-xl shadow-sm border p-8">
              {/* Question Header */}
              <div className="flex items-center gap-2 mb-4">
                <span className="px-3 py-1 bg-purple-100 text-purple-700 text-sm rounded-full font-semibold">
                  Question {currentQuestionIndex + 1} of {questions.length}
                </span>
                {currentQuestion?.required && (
                  <span className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded font-medium">
                    Required
                  </span>
                )}
              </div>

              {/* Question Text */}
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                {currentQuestion?.questionText}
              </h3>

              {currentQuestion?.helpText && (
                <p className="text-sm text-gray-500 mb-6 flex items-start gap-2">
                  <Info size={14} className="mt-0.5 shrink-0" />
                  {currentQuestion.helpText}
                </p>
              )}

              {/* Answer Input */}
              <div className="mt-6">
                <QuestionInput 
                  question={currentQuestion}
                  value={responses[currentQuestion?.questionId] || ''}
                  onChange={(value) => handleResponseChange(currentQuestion?.questionId, value)}
                  onFileUpload={(files) => handleFileUpload(currentQuestion?.questionId, files)}
                  onRemoveFile={(index) => removeFile(currentQuestion?.questionId, index)}
                  uploadedFiles={uploadedFiles[currentQuestion?.questionId] || []}
                />
              </div>
            </div>

            {/* Navigation */}
            <div className="flex items-center justify-between mt-6">
              <div className="flex items-center gap-2">
                {currentQuestionIndex > 0 && (
                  <button
                    onClick={() => setCurrentQuestionIndex(prev => prev - 1)}
                    className="flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <ChevronLeft size={18} />
                    Previous
                  </button>
                )}

                <button
                  onClick={handleSaveDraft}
                  disabled={savingDraft}
                  className="flex items-center gap-2 px-4 py-2.5 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <Save size={16} />
                  {savingDraft ? 'Saving...' : 'Save Draft'}
                </button>
              </div>

              {!isLastQuestion ? (
                <button
                  onClick={() => setCurrentQuestionIndex(prev => prev + 1)}
                  className="flex items-center gap-2 px-6 py-2.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-semibold"
                >
                  Next
                  <ChevronRight size={18} />
                </button>
              ) : (
                <button
                  onClick={handleSubmit}
                  disabled={submitting || !allRequiredAnswered}
                  className="flex items-center gap-2 px-6 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? (
                    <>
                      <RefreshCw size={18} className="animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Check size={18} />
                      Submit Survey
                    </>
                  )}
                </button>
              )}
            </div>

            {/* Question Navigation Dots */}
            <div className="flex items-center justify-center gap-2 mt-6">
              {questions.map((q, idx) => {
                const isAnswered = responses[q.questionId] !== '' && 
                  responses[q.questionId] !== null && 
                  responses[q.questionId] !== undefined;
                const isCurrent = idx === currentQuestionIndex;
                
                return (
                  <button
                    key={q.questionId || idx}
                    onClick={() => setCurrentQuestionIndex(idx)}
                    className={`h-2 rounded-full transition-all ${
                      isCurrent 
                        ? 'w-6 bg-purple-600' 
                        : isAnswered 
                        ? 'w-2 bg-green-500' 
                        : 'w-2 bg-gray-300 hover:bg-gray-400'
                    }`}
                    title={`Question ${idx + 1}`}
                  />
                );
              })}
            </div>
          </div>
        </main>
      )}

      {/* Footer */}
      <footer className="bg-gray-100 border-t py-6 mt-auto">
        <div className="max-w-4xl mx-auto px-4 text-center text-sm text-gray-600">
          <p className="flex items-center justify-center gap-2">
            <Mail size={14} />
            Having trouble? Contact us at sustainability@company.com
          </p>
          <p className="mt-2 text-xs flex items-center justify-center gap-1">
            <Shield size={12} />
            Your responses are securely encrypted and will be used for sustainability reporting purposes only.
          </p>
        </div>
      </footer>
    </div>
  );
}

// Question Input Component
function QuestionInput({ question, value, onChange, onFileUpload, onRemoveFile, uploadedFiles }) {
  if (!question) return null;

  switch (question.questionType) {
    case 'number':
    case 'numeric':
      return (
        <div>
          <div className="flex gap-3">
            <input
              type="number"
              value={value}
              onChange={(e) => onChange(e.target.value)}
              placeholder="Enter value..."
              step={question.validation?.decimalPlaces ? `0.${'0'.repeat(question.validation.decimalPlaces - 1)}1` : '1'}
              min={question.validation?.min}
              max={question.validation?.max}
              className="flex-1 px-4 py-3 border border-gray-300 rounded-lg text-lg focus:border-purple-500 focus:ring-2 focus:ring-purple-200 focus:outline-none"
            />
            {(question.unit || question.validation?.unit) && (
              <div className="flex items-center px-4 py-3 bg-gray-100 border border-gray-300 rounded-lg text-gray-700 font-medium">
                {question.unit || question.validation?.unit}
              </div>
            )}
          </div>
          {question.exampleValue && (
            <p className="text-xs text-gray-500 mt-2">Example: {question.exampleValue}</p>
          )}
        </div>
      );

    case 'text':
      return (
        <div>
          <textarea
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder="Enter your answer..."
            rows={4}
            maxLength={question.validation?.maxLength}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:border-purple-500 focus:ring-2 focus:ring-purple-200 focus:outline-none resize-none"
          />
          {question.validation?.maxLength && (
            <p className="text-xs text-gray-500 mt-1">
              {(value || '').length} / {question.validation.maxLength} characters
            </p>
          )}
        </div>
      );

    case 'select':
      return (
        <div className="space-y-2">
          {(question.options || []).map((option, idx) => (
            <button
              key={idx}
              onClick={() => onChange(option.value)}
              className={`w-full text-left p-4 rounded-lg border-2 transition-colors ${
                value === option.value
                  ? 'border-purple-500 bg-purple-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center gap-3">
                <div className={`h-5 w-5 rounded-full border-2 flex items-center justify-center ${
                  value === option.value
                    ? 'border-purple-600 bg-purple-600'
                    : 'border-gray-300'
                }`}>
                  {value === option.value && (
                    <div className="h-2 w-2 rounded-full bg-white" />
                  )}
                </div>
                <span className="text-sm font-medium text-gray-900">{option.label}</span>
              </div>
            </button>
          ))}
        </div>
      );

    case 'multi_select':
      const selectedValues = Array.isArray(value) ? value : [];
      return (
        <div className="space-y-2">
          {(question.options || []).map((option, idx) => {
            const isSelected = selectedValues.includes(option.value);
            return (
              <button
                key={idx}
                onClick={() => {
                  const newValues = isSelected
                    ? selectedValues.filter(v => v !== option.value)
                    : [...selectedValues, option.value];
                  onChange(newValues);
                }}
                className={`w-full text-left p-4 rounded-lg border-2 transition-colors ${
                  isSelected
                    ? 'border-purple-500 bg-purple-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`h-5 w-5 rounded border-2 flex items-center justify-center ${
                    isSelected
                      ? 'border-purple-600 bg-purple-600'
                      : 'border-gray-300'
                  }`}>
                    {isSelected && <Check size={12} className="text-white" />}
                  </div>
                  <span className="text-sm font-medium text-gray-900">{option.label}</span>
                </div>
              </button>
            );
          })}
        </div>
      );

    case 'boolean':
      return (
        <div className="flex gap-4">
          <button
            onClick={() => onChange(true)}
            className={`flex-1 p-4 rounded-lg border-2 transition-colors flex items-center justify-center gap-2 ${
              value === true
                ? 'border-green-500 bg-green-50'
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <Check size={20} className={value === true ? 'text-green-600' : 'text-gray-400'} />
            <span className="font-medium">Yes</span>
          </button>
          <button
            onClick={() => onChange(false)}
            className={`flex-1 p-4 rounded-lg border-2 transition-colors flex items-center justify-center gap-2 ${
              value === false
                ? 'border-red-500 bg-red-50'
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <X size={20} className={value === false ? 'text-red-600' : 'text-gray-400'} />
            <span className="font-medium">No</span>
          </button>
        </div>
      );

    case 'date':
      return (
        <input
          type="date"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="px-4 py-3 border border-gray-300 rounded-lg focus:border-purple-500 focus:ring-2 focus:ring-purple-200 focus:outline-none"
        />
      );

    case 'date_range':
      const [startDate, endDate] = (value || ' to ').split(' to ');
      return (
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-xs text-gray-600 mb-2">Start Date</label>
            <input
              type="date"
              value={startDate || ''}
              onChange={(e) => onChange(`${e.target.value} to ${endDate || ''}`)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:border-purple-500 focus:ring-2 focus:ring-purple-200 focus:outline-none"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-2">End Date</label>
            <input
              type="date"
              value={endDate || ''}
              onChange={(e) => onChange(`${startDate || ''} to ${e.target.value}`)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:border-purple-500 focus:ring-2 focus:ring-purple-200 focus:outline-none"
            />
          </div>
        </div>
      );

    case 'currency':
      return (
        <div className="flex gap-3">
          <div className="flex items-center px-4 py-3 bg-gray-100 border border-gray-300 rounded-lg text-gray-700 font-medium">
            $
          </div>
          <input
            type="number"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder="0.00"
            step="0.01"
            className="flex-1 px-4 py-3 border border-gray-300 rounded-lg text-lg focus:border-purple-500 focus:ring-2 focus:ring-purple-200 focus:outline-none"
          />
        </div>
      );

    case 'file_upload':
      return (
        <div>
          <div className="p-8 border-2 border-dashed border-gray-300 rounded-lg text-center hover:border-purple-400 hover:bg-purple-50 transition-colors cursor-pointer">
            <input
              type="file"
              onChange={(e) => onFileUpload(e.target.files)}
              multiple={question.validation?.maxFiles > 1}
              accept={question.validation?.allowedFormats?.map(f => `.${f}`).join(',')}
              className="hidden"
              id={`file-upload-${question.questionId}`}
            />
            <label htmlFor={`file-upload-${question.questionId}`} className="cursor-pointer">
              <Upload className="mx-auto text-gray-400 mb-3" size={32} />
              <p className="text-sm font-medium text-gray-900 mb-1">
                Click to upload or drag and drop
              </p>
              <p className="text-xs text-gray-500">
                {question.validation?.allowedFormats?.join(', ').toUpperCase() || 'PDF, XLSX, PNG, JPG'}
                {question.validation?.maxSizeMB && ` (Max ${question.validation.maxSizeMB}MB per file)`}
              </p>
            </label>
          </div>

          {/* Uploaded Files List */}
          {Array.isArray(value) && value.length > 0 && (
            <div className="mt-4 space-y-2">
              {value.map((file, idx) => (
                <div key={idx} className="flex items-center gap-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                  <CheckCircle className="text-green-600" size={18} />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">{file.fileName}</p>
                    <p className="text-xs text-gray-500">
                      {(file.fileSize / 1024 / 1024).toFixed(2)} MB
                    </p>
                  </div>
                  <button
                    onClick={() => onRemoveFile(idx)}
                    className="text-red-600 hover:text-red-700 p-2 hover:bg-red-50 rounded"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      );

    default:
      return (
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Enter your response..."
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:border-purple-500 focus:ring-2 focus:ring-purple-200 focus:outline-none"
        />
      );
  }
}
